import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("fa_token") || "");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fa_cart") || "[]"); } catch { return []; }
  });
  const [authOpen, setAuthOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      setSettings(r.data);
      // Apply CSS variables
      const root = document.documentElement;
      root.style.setProperty('--brand-copper', r.data.accent_color || '#B87333');
      root.style.setProperty('--text-primary', r.data.text_primary || '#1E1F1E');
      root.style.setProperty('--bg-primary', r.data.bg_primary || '#FAF9F6');
      root.style.setProperty('--bg-secondary', r.data.bg_secondary || '#F3EFE9');
    });
  }, []);

  const refreshSettings = async () => {
    const r = await axios.get(`${API}/settings`);
    setSettings(r.data);
    const root = document.documentElement;
    root.style.setProperty('--brand-copper', r.data.accent_color);
    root.style.setProperty('--text-primary', r.data.text_primary);
    root.style.setProperty('--bg-primary', r.data.bg_primary);
    root.style.setProperty('--bg-secondary', r.data.bg_secondary);
    return r.data;
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => { setToken(""); localStorage.removeItem("fa_token"); });
    }
  }, [token]);

  useEffect(() => {
    localStorage.setItem("fa_cart", JSON.stringify(cart));
  }, [cart]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setToken(r.data.token); localStorage.setItem("fa_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const signup = async (name, email, password) => {
    const r = await axios.post(`${API}/auth/signup`, { name, email, password });
    setToken(r.data.token); localStorage.setItem("fa_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const logout = () => { setToken(""); setUser(null); localStorage.removeItem("fa_token"); };

  const addToCart = (product, variant, qty = 1) => {
    setCart(prev => {
      const idx = prev.findIndex(c => c.product_id === product.id && c.size === variant.size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        product_id: product.id, name: product.name, image_url: product.image_url,
        size: variant.size, price: variant.price, quantity: qty,
      }];
    });
  };
  const updateQty = (product_id, size, qty) => {
    setCart(prev => prev.map(c => (c.product_id === product_id && c.size === size) ? { ...c, quantity: Math.max(1, qty) } : c));
  };
  const removeFromCart = (product_id, size) => {
    setCart(prev => prev.filter(c => !(c.product_id === product_id && c.size === size)));
  };
  const clearCart = () => setCart([]);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const value = { API, user, token, cart, authOpen, setAuthOpen, settings, refreshSettings,
    login, signup, logout, addToCart, updateQty, removeFromCart, clearCart, authHeaders };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => useContext(AppCtx);

export const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

/** Returns the lowest price across all variants of a product. */
export const minVariantPrice = (product) => {
  const vs = product?.variants || [];
  if (!vs.length) return 0;
  return Math.min(...vs.map(v => Number(v.price) || Infinity));
};

/** Returns the highest price across all variants of a product. */
export const maxVariantPrice = (product) => {
  const vs = product?.variants || [];
  if (!vs.length) return 0;
  return Math.max(...vs.map(v => Number(v.price) || 0));
};

/**
 * Optimize image URLs on-the-fly for fast loading.
 * - Unsplash URLs: serve smaller WebP via Imgix params
 * - Local /api/files uploads: backend resizes + transcodes to WebP with cache
 * - Emergent customer-assets: route via images.weserv.nl proxy (auto-WebP + resize)
 * - Other URLs: returned as-is
 * `q` defaults to 72 (sweet-spot: near-lossless perception, ~40% smaller than q=90).
 */
export const optimizedImage = (url, width = 800, q = 72) => {
  if (!url) return "";
  if (url.includes("images.unsplash.com")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${width}&q=${q}&auto=format&fit=crop&fm=webp`;
  }
  if (url.includes("/api/files/")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${width}&q=${q}`;
  }
  if (url.includes("customer-assets.emergentagent.com")) {
    const bare = url.replace(/^https?:\/\//, "");
    return `https://images.weserv.nl/?url=${encodeURIComponent(bare)}&w=${width}&q=${q}&output=webp`;
  }
  return url;
};

/**
 * Return a `srcset` string for responsive images.
 * Browsers pick the smallest variant that fits actual display dimensions.
 * On mobile (400px column) a phone loads the 400w variant → 60% smaller than 900w.
 * Pass widths in ascending order, e.g. [400, 600, 900].
 */
export const imageSrcSet = (url, widths = [400, 600, 900], q = 72) => {
  if (!url) return "";
  return widths.map(w => `${optimizedImage(url, w, q)} ${w}w`).join(", ");
};

/**
 * Category-appropriate Unsplash fallback used when a product's own image URL fails to load
 * (e.g., an /api/files/ URL that was wiped in an ephemeral disk redeploy before GridFS mirroring).
 * Keeps the storefront visually intact instead of showing broken tiles.
 */
const CATEGORY_FALLBACK = {
  "Floral & Absolute": "https://images.unsplash.com/photo-1490750967868-88aa4486c946",
  "Attar": "https://images.unsplash.com/photo-1583407723467-9b2d22504831",
  "Wood & Resin": "https://images.unsplash.com/photo-1611532736597-de2d4265fba3",
  "Herb & Grass": "https://images.unsplash.com/photo-1499002238440-d264edd596ec",
  "Resinoid": "https://images.unsplash.com/photo-1611532736597-de2d4265fba3",
  "Carrier Oil": "https://images.unsplash.com/photo-1611928482473-7b27d24eab80",
};
const DEFAULT_FALLBACK = "https://images.unsplash.com/photo-1490750967868-88aa4486c946";

export const fallbackImage = (product, width = 700) => {
  const cat = product?.category || "";
  const base = CATEGORY_FALLBACK[cat] || DEFAULT_FALLBACK;
  return optimizedImage(base, width, 70);
};

/**
 * onError handler factory for product <img> tags.
 * If the primary product image URL fails, swap in a category-appropriate placeholder.
 * Marks the element with a data attribute to prevent infinite retry loops.
 */
export const handleImgError = (product) => (e) => {
  const img = e.currentTarget;
  if (img.dataset.fallbackApplied) return;
  img.dataset.fallbackApplied = "1";
  img.removeAttribute("srcset");
  img.src = fallbackImage(product, 700);
};
